import _ from "lodash";
/** -- external -- */
import React, { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import classNames from "classnames";

/** -- internal -- */
import { useI18n } from "Hooks";
import PaginationLoader from "UIComponents/PaginationLoader";
import { useHandleScroll } from "Utils/CustomHooks";
import { Button } from "@toddle-edu/ds-web";

/** -- relative -- */
import classes from "./CourseFeed.scss";
import {
  CourseDetails,
  SearchWithDeBounce,
  EmptyPlaceholder,
  CourseFeedSkeletonLoader,
} from "./components";
import {
  goToCourseRoute,
  goToLearningCourseRoute,
} from "GlobalHeader/modules/Module";
import CourseFeedWrapperForPaidProgram from "./CourseFeedWrapperForPaidProgram";
import CourseFeedWrapperForFreeProgram from "./CourseFeedWrapperForFreeProgram";
import { HeadingText } from "../../commonComponents";
import {
  getIsGlobalSearchJumpToRevampEnabled,
  trackJumpToCardClick,
  JUMP_TO_COLUMNS,
} from "GlobalHeader/modules/Utils";

const CourseFeedV2 = ({
  data,
  networkStatus,
  hasNextPage,
  loadMoreFeed,
  searchText,
  updateAvailableCourses,
  isLearningCoursesEnabled,
  isLearningCoursesFeed,
  t,
  isData,
  isLoading,
  columnIndex,
  testId,
  onCourseClick,
}) => {
  useEffect(() => {
    const first9Courses = _.slice(data, 0, 9);
    const courses = _.map(first9Courses, edge => edge.node);
    updateAvailableCourses?.(courses);
  }, [data]);

  return (
    <div
      className={classNames(classes.courseFeedContainerV2, {
        [classes.gap]: isLoading && !isData,
      })}
    >
      {isLoading && !isData ? (
        <CourseFeedSkeletonLoader dummyCourseCount={10} showSubtitle={false} />
      ) : (
        <>
          {_.map(data, (edge, index) => {
            const courseId = _.get(edge, "node.id");
            const learningCourseId = _.get(
              edge,
              "node.learningCourse[0].id",
              null
            );

            return (
              <CourseDetails
                key={courseId}
                course={edge.node}
                testId={testId}
                columnIndex={columnIndex}
                courseOrder={index <= 8 ? index + 1 : null}
                isLearningCoursesEnabled={isLearningCoursesEnabled}
                isLearningCoursesFeed={isLearningCoursesFeed}
                onCourseClick={onCourseClick({ courseId, learningCourseId })}
              />
            );
          })}
          {hasNextPage && networkStatus !== 3 ? (
            <div className={classes.seeMoreButtonWrapper}>
              <Button
                className={classes.seeMoreButton}
                variant="progressive"
                type="inline"
                dsVersion="2.0"
                onClick={loadMoreFeed}
                data-elm={`jump-to-col-${columnIndex}`}
                tabIndex="-1"
                test-id="seeMoreButton"
                testId={`${testId}-seeMoreButton`}
              >
                {t("common:see_more_noDots")}
              </Button>
            </div>
          ) : null}
          {networkStatus === 3 ? (
            <div className={classes.skeletonLoaderWrapper}>
              <CourseFeedSkeletonLoader showSubtitle={false} />
            </div>
          ) : null}
          {_.isEmpty(data) ? (
            <EmptyPlaceholder
              isLearningCoursesFeed={isLearningCoursesFeed}
              searchText={searchText}
            />
          ) : null}
        </>
      )}
    </div>
  );
};

const CourseFeed = ({
  data,
  networkStatus,
  hasNextPage,
  loadMoreFeed,
  searchText,
  testId,
  updateAvailableCourses,
  isLearningCoursesEnabled,
  courseGradeFilters,
  onCourseClick,
}) => {
  const containerRef = useRef();

  const localTestId = `${testId}-courseFeed`;

  useHandleScroll({
    networkStatus,
    loadMoreFeed,
    hasNextPage,
    getScrollRef: () => containerRef.current,
  });

  useEffect(() => {
    const first9Courses = _.slice(data, 0, 9);
    const courses = _.map(first9Courses, edge => edge.node);
    updateAvailableCourses?.(courses);
  }, [data]);

  return (
    <div className={classes.courseFeedContainer} ref={containerRef}>
      {_.map(data, (edge, index) => {
        const courseId = _.get(edge, "node.id");
        const learningCourseId = _.get(edge, "node.learningCourse[0].id", null);

        const testId = `${localTestId}-courseList-${courseId}`;
        return (
          <CourseDetails
            key={courseId}
            course={edge.node}
            testId={testId}
            courseOrder={index <= 8 ? index + 1 : null}
            onCourseClick={onCourseClick({ courseId, learningCourseId })}
            isLearningCoursesEnabled={isLearningCoursesEnabled}
            courseGradeFilters={courseGradeFilters}
          />
        );
      })}
      {networkStatus === 3 ? <PaginationLoader /> : null}
      {_.isEmpty(data) ? <EmptyPlaceholder searchText={searchText} /> : null}
    </div>
  );
};

const CourseFeedWithFilters = props => {
  const {
    testId,
    goToCourseRoute,
    goToLearningCourseRoute,
    onNavigationDone,
    updateAvailableCourses,
    goToPlatformHomeChildRoute,
    isCurriculumProgramFree,
    isLearningCoursesEnabled,
    feedType,
    columnIndex,
  } = props;

  const [searchText, setSearchText] = useState("");
  const localTestId = `${testId}-coursesFeed`;

  const isLearningCoursesFeed =
    feedType == JUMP_TO_COLUMNS.LEARNING_COURSE_FEED;

  const { t } = useI18n(["common"]);

  const renderCourseFeed = ({
    data,
    networkStatus,
    hasNextPage,
    loadMoreFeed,
    courseGradeFilters,
    isLoading,
    isData,
  }) => {
    // Reorder courses based on bookmarks
    const coursesWithOrder = _.orderBy(
      data,
      [
        course =>
          _.get(
            course,
            `node.${isLearningCoursesFeed ? "isBookmarked" : "isBookmark"}`,
            false
          ),
      ],
      "desc"
    );

    const FeedComponent = isGlobalSearchJumpToRevampEnabled
      ? CourseFeedV2
      : CourseFeed;

    const onCourseClick = ({ courseId, learningCourseId }) => e => {
      const isMouseClick = !!e.isTrusted;
      trackJumpToCardClick({
        e,
        featureType: feedType,
        featureId: courseId,
        isMouseClick,
      });

      if (isLearningCoursesFeed) {
        goToLearningCourseRoute({
          learningCourseId: courseId,
        });
      } else {
        goToCourseRoute({
          courseId,
          learningCourseId,
        });
      }
      onNavigationDone();
    };

    return (
      <FeedComponent
        data={coursesWithOrder}
        testId={localTestId}
        isLearningCoursesFeed={isLearningCoursesFeed}
        networkStatus={networkStatus}
        hasNextPage={hasNextPage}
        loadMoreFeed={loadMoreFeed}
        onNavigationDone={onNavigationDone}
        updateAvailableCourses={updateAvailableCourses}
        searchText={searchText}
        isLearningCoursesEnabled={isLearningCoursesEnabled}
        goToPlatformHomeChildRoute={goToPlatformHomeChildRoute}
        courseGradeFilters={courseGradeFilters}
        isData={isData}
        isLoading={isLoading}
        columnIndex={columnIndex}
        onCourseClick={onCourseClick}
        t={t}
      />
    );
  };

  const isGlobalSearchJumpToRevampEnabled = getIsGlobalSearchJumpToRevampEnabled();

  const containerClasses = isGlobalSearchJumpToRevampEnabled
    ? classes.containerV2
    : classes.container;

  const courseFeedWrapperContainerClasses = isGlobalSearchJumpToRevampEnabled
    ? classes.courseFeedWrapperContainerV2
    : classes.courseFeedWrapperContainer;

  return (
    <div className={containerClasses}>
      {isGlobalSearchJumpToRevampEnabled ? (
        <HeadingText
          text={
            isLearningCoursesFeed
              ? t("course:courses")
              : t("common:class_other")
          }
        />
      ) : (
        <div className={classes.searchContainer}>
          <SearchWithDeBounce
            testId={localTestId}
            isLearningCoursesFeed={isLearningCoursesFeed}
            onChangeSearchText={setSearchText}
          />
        </div>
      )}
      <div className={courseFeedWrapperContainerClasses}>
        {!isCurriculumProgramFree || isLearningCoursesFeed ? (
          <CourseFeedWrapperForPaidProgram
            isLearningCoursesFeed={isLearningCoursesFeed}
            searchText={searchText}
          >
            {renderCourseFeed}
          </CourseFeedWrapperForPaidProgram>
        ) : (
          <CourseFeedWrapperForFreeProgram
            isLearningCoursesFeed={isLearningCoursesFeed}
            searchText={searchText}
          >
            {renderCourseFeed}
          </CourseFeedWrapperForFreeProgram>
        )}
      </div>
    </div>
  );
};

const mapActionCreators = {
  goToCourseRoute,
  goToLearningCourseRoute,
};

export default connect(null, mapActionCreators)(CourseFeedWithFilters);
